(function(root){
  root.OpenSeadragon = root.OpenSeadragon || {};
  // Full inlined worker source
  root.OpenSeadragon.__GEOJSON_WORKER_SOURCE__ = `
/**
 * Worker-side half of the FlexDrawer HTTP bridge.
 *
 * This shim is concatenated into every FlexDrawer worker blob. It exposes
 * \`self.requestFetch(url, init)\` for the worker's network call sites to use in
 * place of \`fetch(...)\` when the main thread has wired an HttpAdapter via
 * installHttpBridge() (see http-bridge.main.js).
 *
 * Routing is gated by \`self.__hasHttpBridge\`: it starts false and flips to true
 * only after the main side posts \`{ type: 'http:bridge-on' }\`. When the flag is
 * false, call sites must fall back to native fetch — so worker code stays
 * usable standalone (no adapter wired) without any behavioral change.
 *
 * Protocol (worker ↔ main):
 *   worker → main: { type: 'http:request', id, url, init }
 *   main → worker: { type: 'http:response', id, status, ok, headers, body }
 *                 ({ body } is a transferable ArrayBuffer; zero-copy)
 *   main → worker: { type: 'http:error', id, message, name }
 *   worker → main: { type: 'http:cancel', id }
 *
 * Response objects returned from \`requestFetch\` expose status / ok / headers
 * plus async arrayBuffer() / json() / text() — the same surface the existing
 * worker call sites already use on real \`Response\` instances.
 */
(function attachHttpBridge(scope) {
    if (scope.requestFetch) {
        // already attached — ignore double inclusion
        return;
    }

    scope.__hasHttpBridge = false;

    var nextId = 1;
    var pending = new Map(); // id -> { resolve, reject, signal, abortListener }

    scope.addEventListener('message', function (event) {
        var msg = event.data;
        if (!msg || typeof msg.type !== 'string' || msg.type.indexOf('http:') !== 0) {
            return;
        }

        if (msg.type === 'http:bridge-on') {
            scope.__hasHttpBridge = true;
            return;
        }

        if (msg.type === 'http:bridge-off') {
            scope.__hasHttpBridge = false;
            return;
        }

        var waiter = pending.get(msg.id);
        if (!waiter) {
            return;
        }

        pending.delete(msg.id);
        detachAbortListener(waiter);

        if (msg.type === 'http:response') {
            waiter.resolve(makeResponseShim(msg));
        } else if (msg.type === 'http:error') {
            var err = new Error(msg.message || 'flex-renderer http bridge: request failed');
            if (msg.name) {
                err.name = msg.name;
            }
            waiter.reject(err);
        }
    });

    scope.requestFetch = function requestFetch(url, init) {
        var id = nextId++;
        var safeInit;
        try {
            safeInit = serializeInit(init);
        } catch (err) {
            return Promise.reject(err);
        }

        var signal = init && init.signal;
        if (signal && signal.aborted) {
            return Promise.reject(makeAbortError(signal.reason));
        }

        return new Promise(function (resolve, reject) {
            var waiter = {
                resolve: resolve,
                reject: reject,
                signal: signal || null,
                abortListener: null
            };
            pending.set(id, waiter);

            try {
                scope.postMessage({
                    type: 'http:request',
                    id: id,
                    url: url,
                    init: safeInit
                });
            } catch (err) {
                pending.delete(id);
                reject(err);
                return;
            }

            if (signal) {
                waiter.abortListener = function () {
                    if (!pending.has(id)) {
                        return;
                    }
                    pending.delete(id);
                    try {
                        scope.postMessage({ type: 'http:cancel', id: id });
                    } catch (_) { /* ignore */ }
                    reject(makeAbortError(signal.reason));
                };
                signal.addEventListener('abort', waiter.abortListener, { once: true });
            }
        });
    };

    function detachAbortListener(waiter) {
        if (waiter.signal && waiter.abortListener) {
            try {
                waiter.signal.removeEventListener('abort', waiter.abortListener);
            } catch (_) { /* ignore */ }
            waiter.abortListener = null;
        }
    }

    function makeResponseShim(message) {
        var status = message.status;
        var ok = message.ok;
        var headers = message.headers || {};
        var body = message.body;

        return {
            status: status,
            ok: ok,
            headers: headers,
            arrayBuffer: function () { return Promise.resolve(body); },
            json: function () {
                try {
                    return Promise.resolve(JSON.parse(decodeBody(body)));
                } catch (err) {
                    return Promise.reject(err);
                }
            },
            text: function () {
                return Promise.resolve(decodeBody(body));
            }
        };
    }

    function decodeBody(body) {
        if (!body) {
            return '';
        }
        // body is always an ArrayBuffer when present
        return new TextDecoder('utf-8').decode(new Uint8Array(body));
    }

    function makeAbortError(reason) {
        var message = typeof reason === 'string' ? reason : (reason && reason.message) || 'aborted';
        // DOMException is available in workers in all targeted runtimes
        try {
            return new DOMException(message, 'AbortError');
        } catch (_) {
            var err = new Error(message);
            err.name = 'AbortError';
            return err;
        }
    }

    function serializeInit(init) {
        var out = {};
        if (!init || typeof init !== 'object') {
            return out;
        }

        if (init.method) {
            out.method = String(init.method);
        }

        if (init.headers) {
            out.headers = flattenHeaders(init.headers);
        }

        if (init.body !== undefined && init.body !== null) {
            var body = init.body;
            if (typeof body === 'string' || body instanceof ArrayBuffer) {
                out.body = body;
            } else if (ArrayBuffer.isView && ArrayBuffer.isView(body)) {
                out.body = body;
            } else {
                throw new TypeError('flex-renderer http bridge: unsupported request body type');
            }
        }

        if (init.credentials) {
            out.credentials = init.credentials;
        }
        if (init.cache) {
            out.cache = init.cache;
        }

        return out;
    }

    function flattenHeaders(headers) {
        var out = {};
        if (!headers) {
            return out;
        }
        if (typeof headers.forEach === 'function' && !Array.isArray(headers)) {
            // Headers instance or Map-like
            headers.forEach(function (value, key) {
                out[String(key).toLowerCase()] = String(value);
            });
            return out;
        }
        if (Array.isArray(headers)) {
            for (var i = 0; i < headers.length; i++) {
                var entry = headers[i];
                if (Array.isArray(entry) && entry.length >= 2) {
                    out[String(entry[0]).toLowerCase()] = String(entry[1]);
                }
            }
            return out;
        }
        if (typeof headers === 'object') {
            for (var key in headers) {
                if (Object.prototype.hasOwnProperty.call(headers, key)) {
                    out[key.toLowerCase()] = String(headers[key]);
                }
            }
        }
        return out;
    }
})(self);

(function(){function r(e,n,t){function o(i,f){if(!n[i]){if(!e[i]){var c="function"==typeof require&&require;if(!f&&c)return c(i,!0);if(u)return u(i,!0);var a=new Error("Cannot find module '"+i+"'");throw a.code="MODULE_NOT_FOUND",a}var p=n[i]={exports:{}};e[i][0].call(p.exports,function(r){var n=e[i][1][r];return o(n||r)},p,p.exports,r,e,n,t)}return n[i].exports}for(var u="function"==typeof require&&require,i=0;i<t.length;i++)o(t[i]);return o}return r})()({1:[function(require,module,exports){"use strict";var _earcut=_interopRequireDefault(require("earcut"));function _interopRequireDefault(e){return e&&e.__esModule?e:{default:e}}self.earcut=_earcut.default},{earcut:2}],2:[function(require,module,exports){"use strict";Object.defineProperty(exports,"__esModule",{value:true});exports.default=earcut;exports.deviation=deviation;exports.flatten=flatten;function earcut(data,holeIndices,dim=2){const hasHoles=holeIndices&&holeIndices.length;const outerLen=hasHoles?holeIndices[0]*dim:data.length;let outerNode=linkedList(data,0,outerLen,dim,true);const triangles=[];if(!outerNode||outerNode.next===outerNode.prev)return triangles;let minX,minY,invSize;if(hasHoles)outerNode=eliminateHoles(data,holeIndices,outerNode,dim);if(data.length>80*dim){minX=data[0];minY=data[1];let maxX=minX;let maxY=minY;for(let i=dim;i<outerLen;i+=dim){const x=data[i];const y=data[i+1];if(x<minX)minX=x;if(y<minY)minY=y;if(x>maxX)maxX=x;if(y>maxY)maxY=y}invSize=Math.max(maxX-minX,maxY-minY);invSize=invSize!==0?32767/invSize:0}earcutLinked(outerNode,triangles,dim,minX,minY,invSize,0);return triangles}function linkedList(data,start,end,dim,clockwise){let last;if(clockwise===signedArea(data,start,end,dim)>0){for(let i=start;i<end;i+=dim)last=insertNode(i/dim|0,data[i],data[i+1],last)}else{for(let i=end-dim;i>=start;i-=dim)last=insertNode(i/dim|0,data[i],data[i+1],last)}if(last&&equals(last,last.next)){removeNode(last);last=last.next}return last}function filterPoints(start,end){if(!start)return start;if(!end)end=start;let p=start,again;do{again=false;if(!p.steiner&&(equals(p,p.next)||area(p.prev,p,p.next)===0)){removeNode(p);p=end=p.prev;if(p===p.next)break;again=true}else{p=p.next}}while(again||p!==end);return end}function earcutLinked(ear,triangles,dim,minX,minY,invSize,pass){if(!ear)return;if(!pass&&invSize)indexCurve(ear,minX,minY,invSize);let stop=ear;while(ear.prev!==ear.next){const prev=ear.prev;const next=ear.next;if(invSize?isEarHashed(ear,minX,minY,invSize):isEar(ear)){triangles.push(prev.i,ear.i,next.i);removeNode(ear);ear=next.next;stop=next.next;continue}ear=next;if(ear===stop){if(!pass){earcutLinked(filterPoints(ear),triangles,dim,minX,minY,invSize,1)}else if(pass===1){ear=cureLocalIntersections(filterPoints(ear),triangles);earcutLinked(ear,triangles,dim,minX,minY,invSize,2)}else if(pass===2){splitEarcut(ear,triangles,dim,minX,minY,invSize)}break}}}function isEar(ear){const a=ear.prev,b=ear,c=ear.next;if(area(a,b,c)>=0)return false;const ax=a.x,bx=b.x,cx=c.x,ay=a.y,by=b.y,cy=c.y;const x0=Math.min(ax,bx,cx),y0=Math.min(ay,by,cy),x1=Math.max(ax,bx,cx),y1=Math.max(ay,by,cy);let p=c.next;while(p!==a){if(p.x>=x0&&p.x<=x1&&p.y>=y0&&p.y<=y1&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,p.x,p.y)&&area(p.prev,p,p.next)>=0)return false;p=p.next}return true}function isEarHashed(ear,minX,minY,invSize){const a=ear.prev,b=ear,c=ear.next;if(area(a,b,c)>=0)return false;const ax=a.x,bx=b.x,cx=c.x,ay=a.y,by=b.y,cy=c.y;const x0=Math.min(ax,bx,cx),y0=Math.min(ay,by,cy),x1=Math.max(ax,bx,cx),y1=Math.max(ay,by,cy);const minZ=zOrder(x0,y0,minX,minY,invSize),maxZ=zOrder(x1,y1,minX,minY,invSize);let p=ear.prevZ,n=ear.nextZ;while(p&&p.z>=minZ&&n&&n.z<=maxZ){if(p.x>=x0&&p.x<=x1&&p.y>=y0&&p.y<=y1&&p!==a&&p!==c&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,p.x,p.y)&&area(p.prev,p,p.next)>=0)return false;p=p.prevZ;if(n.x>=x0&&n.x<=x1&&n.y>=y0&&n.y<=y1&&n!==a&&n!==c&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,n.x,n.y)&&area(n.prev,n,n.next)>=0)return false;n=n.nextZ}while(p&&p.z>=minZ){if(p.x>=x0&&p.x<=x1&&p.y>=y0&&p.y<=y1&&p!==a&&p!==c&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,p.x,p.y)&&area(p.prev,p,p.next)>=0)return false;p=p.prevZ}while(n&&n.z<=maxZ){if(n.x>=x0&&n.x<=x1&&n.y>=y0&&n.y<=y1&&n!==a&&n!==c&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,n.x,n.y)&&area(n.prev,n,n.next)>=0)return false;n=n.nextZ}return true}function cureLocalIntersections(start,triangles){let p=start;do{const a=p.prev,b=p.next.next;if(!equals(a,b)&&intersects(a,p,p.next,b)&&locallyInside(a,b)&&locallyInside(b,a)){triangles.push(a.i,p.i,b.i);removeNode(p);removeNode(p.next);p=start=b}p=p.next}while(p!==start);return filterPoints(p)}function splitEarcut(start,triangles,dim,minX,minY,invSize){let a=start;do{let b=a.next.next;while(b!==a.prev){if(a.i!==b.i&&isValidDiagonal(a,b)){let c=splitPolygon(a,b);a=filterPoints(a,a.next);c=filterPoints(c,c.next);earcutLinked(a,triangles,dim,minX,minY,invSize,0);earcutLinked(c,triangles,dim,minX,minY,invSize,0);return}b=b.next}a=a.next}while(a!==start)}function eliminateHoles(data,holeIndices,outerNode,dim){const queue=[];for(let i=0,len=holeIndices.length;i<len;i++){const start=holeIndices[i]*dim;const end=i<len-1?holeIndices[i+1]*dim:data.length;const list=linkedList(data,start,end,dim,false);if(list===list.next)list.steiner=true;queue.push(getLeftmost(list))}queue.sort(compareXYSlope);for(let i=0;i<queue.length;i++){outerNode=eliminateHole(queue[i],outerNode)}return outerNode}function compareXYSlope(a,b){let result=a.x-b.x;if(result===0){result=a.y-b.y;if(result===0){const aSlope=(a.next.y-a.y)/(a.next.x-a.x);const bSlope=(b.next.y-b.y)/(b.next.x-b.x);result=aSlope-bSlope}}return result}function eliminateHole(hole,outerNode){const bridge=findHoleBridge(hole,outerNode);if(!bridge){return outerNode}const bridgeReverse=splitPolygon(bridge,hole);filterPoints(bridgeReverse,bridgeReverse.next);return filterPoints(bridge,bridge.next)}function findHoleBridge(hole,outerNode){let p=outerNode;const hx=hole.x;const hy=hole.y;let qx=-Infinity;let m;if(equals(hole,p))return p;do{if(equals(hole,p.next))return p.next;else if(hy<=p.y&&hy>=p.next.y&&p.next.y!==p.y){const x=p.x+(hy-p.y)*(p.next.x-p.x)/(p.next.y-p.y);if(x<=hx&&x>qx){qx=x;m=p.x<p.next.x?p:p.next;if(x===hx)return m}}p=p.next}while(p!==outerNode);if(!m)return null;const stop=m;const mx=m.x;const my=m.y;let tanMin=Infinity;p=m;do{if(hx>=p.x&&p.x>=mx&&hx!==p.x&&pointInTriangle(hy<my?hx:qx,hy,mx,my,hy<my?qx:hx,hy,p.x,p.y)){const tan=Math.abs(hy-p.y)/(hx-p.x);if(locallyInside(p,hole)&&(tan<tanMin||tan===tanMin&&(p.x>m.x||p.x===m.x&&sectorContainsSector(m,p)))){m=p;tanMin=tan}}p=p.next}while(p!==stop);return m}function sectorContainsSector(m,p){return area(m.prev,m,p.prev)<0&&area(p.next,m,m.next)<0}function indexCurve(start,minX,minY,invSize){let p=start;do{if(p.z===0)p.z=zOrder(p.x,p.y,minX,minY,invSize);p.prevZ=p.prev;p.nextZ=p.next;p=p.next}while(p!==start);p.prevZ.nextZ=null;p.prevZ=null;sortLinked(p)}function sortLinked(list){let numMerges;let inSize=1;do{let p=list;let e;list=null;let tail=null;numMerges=0;while(p){numMerges++;let q=p;let pSize=0;for(let i=0;i<inSize;i++){pSize++;q=q.nextZ;if(!q)break}let qSize=inSize;while(pSize>0||qSize>0&&q){if(pSize!==0&&(qSize===0||!q||p.z<=q.z)){e=p;p=p.nextZ;pSize--}else{e=q;q=q.nextZ;qSize--}if(tail)tail.nextZ=e;else list=e;e.prevZ=tail;tail=e}p=q}tail.nextZ=null;inSize*=2}while(numMerges>1);return list}function zOrder(x,y,minX,minY,invSize){x=(x-minX)*invSize|0;y=(y-minY)*invSize|0;x=(x|x<<8)&16711935;x=(x|x<<4)&252645135;x=(x|x<<2)&858993459;x=(x|x<<1)&1431655765;y=(y|y<<8)&16711935;y=(y|y<<4)&252645135;y=(y|y<<2)&858993459;y=(y|y<<1)&1431655765;return x|y<<1}function getLeftmost(start){let p=start,leftmost=start;do{if(p.x<leftmost.x||p.x===leftmost.x&&p.y<leftmost.y)leftmost=p;p=p.next}while(p!==start);return leftmost}function pointInTriangle(ax,ay,bx,by,cx,cy,px,py){return(cx-px)*(ay-py)>=(ax-px)*(cy-py)&&(ax-px)*(by-py)>=(bx-px)*(ay-py)&&(bx-px)*(cy-py)>=(cx-px)*(by-py)}function pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,px,py){return!(ax===px&&ay===py)&&pointInTriangle(ax,ay,bx,by,cx,cy,px,py)}function isValidDiagonal(a,b){return a.next.i!==b.i&&a.prev.i!==b.i&&!intersectsPolygon(a,b)&&(locallyInside(a,b)&&locallyInside(b,a)&&middleInside(a,b)&&(area(a.prev,a,b.prev)||area(a,b.prev,b))||equals(a,b)&&area(a.prev,a,a.next)>0&&area(b.prev,b,b.next)>0)}function area(p,q,r){return(q.y-p.y)*(r.x-q.x)-(q.x-p.x)*(r.y-q.y)}function equals(p1,p2){return p1.x===p2.x&&p1.y===p2.y}function intersects(p1,q1,p2,q2){const o1=sign(area(p1,q1,p2));const o2=sign(area(p1,q1,q2));const o3=sign(area(p2,q2,p1));const o4=sign(area(p2,q2,q1));if(o1!==o2&&o3!==o4)return true;if(o1===0&&onSegment(p1,p2,q1))return true;if(o2===0&&onSegment(p1,q2,q1))return true;if(o3===0&&onSegment(p2,p1,q2))return true;if(o4===0&&onSegment(p2,q1,q2))return true;return false}function onSegment(p,q,r){return q.x<=Math.max(p.x,r.x)&&q.x>=Math.min(p.x,r.x)&&q.y<=Math.max(p.y,r.y)&&q.y>=Math.min(p.y,r.y)}function sign(num){return num>0?1:num<0?-1:0}function intersectsPolygon(a,b){let p=a;do{if(p.i!==a.i&&p.next.i!==a.i&&p.i!==b.i&&p.next.i!==b.i&&intersects(p,p.next,a,b))return true;p=p.next}while(p!==a);return false}function locallyInside(a,b){return area(a.prev,a,a.next)<0?area(a,b,a.next)>=0&&area(a,a.prev,b)>=0:area(a,b,a.prev)<0||area(a,a.next,b)<0}function middleInside(a,b){let p=a;let inside=false;const px=(a.x+b.x)/2;const py=(a.y+b.y)/2;do{if(p.y>py!==p.next.y>py&&p.next.y!==p.y&&px<(p.next.x-p.x)*(py-p.y)/(p.next.y-p.y)+p.x)inside=!inside;p=p.next}while(p!==a);return inside}function splitPolygon(a,b){const a2=createNode(a.i,a.x,a.y),b2=createNode(b.i,b.x,b.y),an=a.next,bp=b.prev;a.next=b;b.prev=a;a2.next=an;an.prev=a2;b2.next=a2;a2.prev=b2;bp.next=b2;b2.prev=bp;return b2}function insertNode(i,x,y,last){const p=createNode(i,x,y);if(!last){p.prev=p;p.next=p}else{p.next=last.next;p.prev=last;last.next.prev=p;last.next=p}return p}function removeNode(p){p.next.prev=p.prev;p.prev.next=p.next;if(p.prevZ)p.prevZ.nextZ=p.nextZ;if(p.nextZ)p.nextZ.prevZ=p.prevZ}function createNode(i,x,y){return{i:i,x:x,y:y,prev:null,next:null,z:0,prevZ:null,nextZ:null,steiner:false}}function deviation(data,holeIndices,dim,triangles){const hasHoles=holeIndices&&holeIndices.length;const outerLen=hasHoles?holeIndices[0]*dim:data.length;let polygonArea=Math.abs(signedArea(data,0,outerLen,dim));if(hasHoles){for(let i=0,len=holeIndices.length;i<len;i++){const start=holeIndices[i]*dim;const end=i<len-1?holeIndices[i+1]*dim:data.length;polygonArea-=Math.abs(signedArea(data,start,end,dim))}}let trianglesArea=0;for(let i=0;i<triangles.length;i+=3){const a=triangles[i]*dim;const b=triangles[i+1]*dim;const c=triangles[i+2]*dim;trianglesArea+=Math.abs((data[a]-data[c])*(data[b+1]-data[a+1])-(data[a]-data[b])*(data[c+1]-data[a+1]))}return polygonArea===0&&trianglesArea===0?0:Math.abs((trianglesArea-polygonArea)/polygonArea)}function signedArea(data,start,end,dim){let sum=0;for(let i=start,j=end-dim;i<end;i+=dim){sum+=(data[j]-data[i])*(data[i+1]+data[j+1]);j=i}return sum}function flatten(data){const vertices=[];const holes=[];const dimensions=data[0][0].length;let holeIndex=0;let prevLen=0;for(const ring of data){for(const p of ring){for(let d=0;d<dimensions;d++)vertices.push(p[d])}if(prevLen){holeIndex+=prevLen;holes.push(holeIndex)}prevLen=ring.length}return{vertices:vertices,holes:holes,dimensions:dimensions}}},{}]},{},[1]);
/**
 * GeoJSON worker for GeoJSONTileSource.
 *
 * Debug-first implementation:
 * - accepts standard GeoJSON Feature, FeatureCollection, raw Geometry, and GeometryCollection objects;
 * - explodes MultiPoint, MultiLineString, MultiPolygon, and GeometryCollection
 *   into simple Point, LineString, and Polygon records before coordinate normalization;
 * - meshes Point, LineString, and Polygon geometries;
 * - can emit LineString geometries as native gl.LINES payloads;
 * - triangulates Polygon rings with earcut, including holes;
 * - uses a top-level GeoJSON bbox as the source coordinate extent;
 * - performs geometry bbox filtering before exact per-tile clipping;
 * - optionally aggregates dense non-max-level tiles into one count badge;
 * - emits FlexDrawer-compatible vector-mesh payloads with vec4 vertices.
 *
 * Vertex format:
 *   [x, y, depth, textureId]
 *
 * where x/y are normalized tile-local coordinates in [0, 1] when inside the
 * tile, depth follows the MVT worker convention, and textureId is -1 for
 * non-textured geometry.
 */

let STATE = {
    configured: false,
    configurePromise: null,
    fatalError: null,
    geometries: [],
    spatialIndex: null,
    tileSize: 512,
    minLevel: 0,
    maxLevel: 0,
    bbox: null,
    width: 1,
    height: 1,
    style: {},
    // Bumped on every style change. Per-geometry resolved colors are cached against
    // this, so a restyle invalidates them without walking every record.
    styleEpoch: 0,
    useNativeLines: false,
    aggregation: null,
    debug: false
};


/**
 * Seven-segment glyph definitions used for aggregate tile count labels.
 *
 * @type {object}
 */
const SEVEN_SEGMENT_GLYPHS = Object.freeze({
    '0': ['top', 'upperRight', 'lowerRight', 'bottom', 'lowerLeft', 'upperLeft'],
    '1': ['upperRight', 'lowerRight'],
    '2': ['top', 'upperRight', 'middle', 'lowerLeft', 'bottom'],
    '3': ['top', 'upperRight', 'middle', 'lowerRight', 'bottom'],
    '4': ['upperLeft', 'middle', 'upperRight', 'lowerRight'],
    '5': ['top', 'upperLeft', 'middle', 'lowerRight', 'bottom'],
    '6': ['top', 'upperLeft', 'middle', 'lowerRight', 'lowerLeft', 'bottom'],
    '7': ['top', 'upperRight', 'lowerRight'],
    '8': ['top', 'upperRight', 'lowerRight', 'bottom', 'lowerLeft', 'upperLeft', 'middle'],
    '9': ['top', 'upperRight', 'lowerRight', 'bottom', 'upperLeft', 'middle'],
    '+': ['middle', 'verticalMiddle']
});


self.onmessage = function(event) {
    const message = event.data || {};

    // The HTTP bridge shim shares this worker's message port and installs its own
    // 'message' listener; both it and self.onmessage see every message. Ignore its
    // traffic here so it never reaches \`default:\` below and fails the whole source.
    if (typeof message.type === 'string' && message.type.indexOf('http:') === 0) {
        return;
    }

    switch (message.type) {
        case 'config':
            STATE.configurePromise = configure(message);
            break;

        case 'style':
            // Style-only update: keep the parsed geometries and the spatial index, and
            // invalidate memoized per-feature colors by bumping the epoch. Re-posting
            // 'config' would refetch the source and rebuild the quadtree.
            STATE.style = message.style;
            STATE.styleEpoch++;
            break;

        case 'tile':
            buildTileWhenReady(message);
            break;

        default:
            self.postMessage({
                type: 'error',
                ok: false,
                error: \`GeoJson worker: unsupported message type \${message.type}\`
            });
    }
};


/**
 * Configure the worker from the tile source.
 *
 * @param {object} message - Configuration message.
 * @returns {Promise<void>} Resolves when the source is ready.
 */
async function configure(message) {
    try {
        if (!self.earcut) {
            throw new Error('GeoJSON worker: missing earcut library.');
        }

        const data = await fetchGeoJSON(message.url);

        STATE = {
            configured: true,
            configurePromise: null,
            fatalError: null,
            geometries: [],
            spatialIndex: null,
            bbox: message.bbox || data.bbox,
            width: message.width,
            height: message.height,
            tileSize: message.tileSize,
            minLevel: message.minLevel,
            maxLevel: message.maxLevel,
            style: message.style,
            styleEpoch: 0,
            useNativeLines: message.useNativeLines === true,
            aggregation: message.aggregation,
            debug: message.debug === true
        };

        const parsed = parseGeojson(data);

        STATE.geometries = parsed.geometries;
        STATE.spatialIndex = createSpatialIndex(STATE.geometries);

        if (parsed.skipped) {
            // Malformed features are skipped rather than failing the source. Report
            // once per configure, not once per bad feature.
            self.postMessage({
                type: 'warning',
                skipped: parsed.skipped,
                total: parsed.total,
                samples: parsed.samples
            });
        }
    } catch (error) {
        // Record the reason so a tile request racing this failure reports the real
        // cause instead of the generic not-configured message.
        STATE.configured = false;
        STATE.fatalError = error.message || String(error);

        self.postMessage({
            type: 'error',
            ok: false,
            error: STATE.fatalError
        });
    }
}


/**
 * Fetch a GeoJSON document.
 *
 * @param {string} url - GeoJSON URL.
 * @returns {Promise<object>} Parsed GeoJSON.
 */
async function fetchGeoJSON(url) {
    if (!url) {
        throw new Error('GeoJSON worker: url is required.');
    }

    const response = self.__hasHttpBridge ? await self.requestFetch(url) : await fetch(url);
    if (!response.ok) {
        throw new Error(\`GeoJSON worker: failed to fetch \${url}: \${response.status}\`);
    }

    return response.json();
}


const GEOMETRY_TYPES = new Set([
    "Point",
    "MultiPoint",
    "LineString",
    "MultiLineString",
    "Polygon",
    "MultiPolygon",
    "GeometryCollection"
]);

/**
 * @typedef {object} SimpleGeoJSONGeometry
 * @property {'Point'|'LineString'|'Polygon'} type - Simple geometry type.
 * @property {*} coordinates - Geometry coordinates in full-resolution image coordinates.
 * @property {number[]} bbox - The geometry's bbox in full-resolution image coordinates.
 * @property {object|null|undefined} properties - Feature properties.
 * @property {string|number|undefined} id - Feature id.
 */

/**
 * @typedef {object} GeoJSONParseResult
 * @property {SimpleGeoJSONGeometry[]} geometries - Simple geometry records.
 * @property {number} total - Feature count seen in the source.
 * @property {number} skipped - Feature count dropped as malformed.
 * @property {string[]} samples - First few skip reasons, for diagnostics.
 */

/**
 * Extract simple GeoJSON geometry records from a GeoJSON object.
 *
 * The renderer only consumes simple Point, LineString, and Polygon records.
 * This reader accepts standard GeoJSON containers and explodes multi-geometries
 * into those simple records while preserving feature properties and id.
 *
 * Supported standard GeoJSON inputs:
 * - FeatureCollection
 * - Feature
 * - a simple geometry type or a GeometryCollection
 *
 * Within a FeatureCollection a malformed feature is skipped and counted, not
 * thrown, so one bad ring cannot take down the whole source. Structural problems
 * with the container itself remain fatal.
 *
 * @param {object} geojson - GeoJSON object.
 * @returns {GeoJSONParseResult} Parsed records and a skip summary.
 * @throws {Error} Thrown when geojson is not a valid GeoJSON object.
 */
function parseGeojson(geojson) {
    if (!geojson || typeof geojson !== 'object' || Array.isArray(geojson)) {
        throw new Error('GeoJSON worker: root GeoJSON value must be a non-array object.');
    }

    if (geojson.type === 'FeatureCollection') {
        return parseFeatureCollection(geojson);
    }

    // A single-feature or bare-geometry root has nothing to degrade to: if it is
    // malformed the source is empty, so let the throw stay fatal.
    if (geojson.type === 'Feature') {
        return { geometries: parseFeature(geojson), total: 1, skipped: 0, samples: [] };
    }

    if (GEOMETRY_TYPES.has(geojson.type)) {
        return { geometries: parseGeometry(geojson), total: 1, skipped: 0, samples: [] };
    }

    throw new Error('GeoJSON worker: root GeoJSON type must be FeatureCollection, Feature, or a supported geometry type.');
}

/**
 * Maximum number of skip reasons retained for diagnostics.
 *
 * @type {number}
 */
const MAX_SKIP_SAMPLES = 5;

/**
 * Parse a standard GeoJSON FeatureCollection.
 *
 * Individual malformed features are skipped and counted instead of aborting the
 * collection: real producer output routinely contains a few bad rings, and one of
 * them must not cost every other feature in the file.
 *
 * A collection in which every feature fails is treated as fatal, because that
 * means the file is not what it claims to be.
 *
 * @param {object} collection - FeatureCollection object.
 * @returns {GeoJSONParseResult} Parsed records and a skip summary.
 * @throws {Error} Thrown when the collection itself is invalid, or when no feature parsed.
 */
function parseFeatureCollection(collection) {
    if (!collection || typeof collection !== 'object' || Array.isArray(collection)) {
        throw new Error('GeoJSON worker: FeatureCollection must be a non-array object.');
    }

    if (collection.type !== 'FeatureCollection') {
        throw new Error('GeoJSON worker: FeatureCollection.type must be "FeatureCollection".');
    }

    if (!Array.isArray(collection.features)) {
        throw new Error('GeoJSON worker: FeatureCollection.features must be an array of Feature objects.');
    }

    const geometries = [];
    const samples = [];
    let skipped = 0;

    for (let index = 0; index < collection.features.length; index++) {
        try {
            const records = parseFeature(collection.features[index]);

            for (const record of records) {
                geometries.push(record);
            }
        } catch (error) {
            skipped++;

            if (samples.length < MAX_SKIP_SAMPLES) {
                samples.push(\`feature[\${index}]: \${error.message || String(error)}\`);
            }
        }
    }

    const total = collection.features.length;

    if (total > 0 && skipped === total) {
        throw new Error(\`GeoJSON worker: every feature failed to parse (\${total}). First reason: \${samples[0]}\`);
    }

    return { geometries, total, skipped, samples };
}

/**
 * Parse a standard GeoJSON Feature.
 *
 * @param {object} feature - Feature object.
 * @returns {SimpleGeoJSONGeometry[]} Simple feature records.
 * @throws {Error} Thrown when feature is not a valid GeoJSON Feature.
 */
function parseFeature(feature) {
    if (!feature || typeof feature !== 'object' || Array.isArray(feature)) {
        throw new Error('GeoJSON worker: Feature must be a non-array object.');
    }

    if (feature.type !== 'Feature') {
        throw new Error('GeoJSON worker: Feature.type must be "Feature".');
    }

    // the GeoJSON standard specifies properties as required,
    // but they are not strictly necessary for rendering so we will be permissive and allow them to be undefined
    if (!(feature.properties === undefined || (typeof feature.properties === 'object' && !Array.isArray(feature.properties)) || feature.properties === null)) {
        throw new Error('GeoJSON worker: Feature.properties must be an object, null, or undefined.');
    }

    if (!(feature.id === undefined || typeof feature.id === 'string' || typeof feature.id === 'number')) {
        throw new Error('GeoJSON worker: Feature.id must be a string, number, or undefined.');
    }

    return parseGeometry(feature.geometry, feature.properties, feature.id);
}

/**
 * Convert a supported standard GeoJSON geometry into internal simple geometry records.
 *
 * Multi* geometries and GeometryCollection are exploded into simple records.
 * Polygon holes are preserved in the coordinate structure for now, but the
 * current mesher still consumes only the exterior ring.
 *
 * @param {object} geometry - GeoJSON geometry.
 * @param {object|null|undefined} properties - Feature properties.
 * @param {string|number|undefined} id - Feature id.
 * @returns {SimpleGeoJSONGeometry[]} Internal simple geometry records.
 * @throws {Error} Thrown when geometry is not a valid GeoJSON geometry object.
 */
function parseGeometry(geometry, properties = undefined, id = undefined) {
    if (!geometry || typeof geometry !== 'object' || Array.isArray(geometry)) {
        throw new Error('GeoJSON worker: geometry must be a non-array GeoJSON geometry object.');
    }

    switch (geometry.type) {
        case 'Point':
            validatePointCoordinates(geometry.coordinates);
            return [ createGeometryRecord('Point', geometry.coordinates, properties, id) ].filter(Boolean);

        case 'LineString':
            validateLineStringCoordinates(geometry.coordinates);
            return [ createGeometryRecord('LineString', geometry.coordinates, properties, id) ].filter(Boolean);

        case 'Polygon':
            validatePolygonCoordinates(geometry.coordinates);
            return [ createGeometryRecord('Polygon', geometry.coordinates, properties, id) ].filter(Boolean);

        case 'MultiPoint':
            if (!Array.isArray(geometry.coordinates)) {
                throw new Error('GeoJSON worker: MultiPoint.coordinates must be an array of Point positions.');
            }

            geometry.coordinates.forEach(validatePointCoordinates);

            return geometry.coordinates.map(coordinate => createGeometryRecord('Point', coordinate, properties, id)).filter(Boolean);

        case 'MultiLineString':
            if (!Array.isArray(geometry.coordinates)) {
                throw new Error('GeoJSON worker: MultiLineString.coordinates must be an array of LineString coordinate arrays.');
            }

            geometry.coordinates.forEach(validateLineStringCoordinates);

            return geometry.coordinates.map(line => createGeometryRecord('LineString', line, properties, id)).filter(Boolean);

        case 'MultiPolygon':
            if (!Array.isArray(geometry.coordinates)) {
                throw new Error('GeoJSON worker: MultiPolygon.coordinates must be an array of Polygon coordinate arrays.');
            }

            geometry.coordinates.forEach(validatePolygonCoordinates);

            return geometry.coordinates.map(polygon => createGeometryRecord('Polygon', polygon, properties, id)).filter(Boolean);

        case 'GeometryCollection':
            if (!Array.isArray(geometry.geometries)) {
                throw new Error('GeoJSON worker: GeometryCollection.geometries must be an array of geometry objects.');
            }

            return geometry.geometries.flatMap(child => parseGeometry(child, properties, id)).filter(Boolean);

        default:
            throw new Error('GeoJSON worker: unsupported or missing GeoJSON geometry type.');
    }
}

/**
 * Validate a GeoJSON Point coordinate array.
 *
 * A Point coordinate must be one GeoJSON position: an array of at least two
 * finite numbers. Extra dimensions are allowed by GeoJSON but are ignored by
 * the current 2D renderer.
 *
 * @param {*} coordinates - Candidate Point coordinates.
 * @returns {void}
 * @throws {Error} Thrown when coordinates are not valid GeoJSON Point coordinates.
 */
function validatePointCoordinates(coordinates) {
    if (!isCoordinates(coordinates)) {
        throw new Error('GeoJSON worker: Point.coordinates must be a GeoJSON position: an array of at least two finite numbers.');
    }
}

/**
 * Validate a GeoJSON LineString coordinate array.
 *
 * A LineString coordinate array must contain at least two valid GeoJSON
 * positions.
 *
 * @param {*} coordinates - Candidate LineString coordinates.
 * @returns {void}
 * @throws {Error} Thrown when coordinates are not valid GeoJSON LineString coordinates.
 */
function validateLineStringCoordinates(coordinates) {
    if (!Array.isArray(coordinates) || coordinates.length < 2 || !coordinates.every(isCoordinates)) {
        throw new Error('GeoJSON worker: LineString.coordinates must be an array containing at least two GeoJSON positions.');
    }
}

/**
 * Validate a GeoJSON Polygon coordinate array.
 *
 * A Polygon coordinate array contains linear rings.
 *
 * @param {*} coordinates - Candidate Polygon coordinates.
 * @returns {void}
 * @throws {Error} Thrown when coordinates are not valid GeoJSON Polygon coordinates.
 */
function validatePolygonCoordinates(coordinates) {
    if (!Array.isArray(coordinates) || !coordinates.every(isLinearRing)) {
        throw new Error('GeoJSON worker: Polygon.coordinates must be an array.');
    }
}

/**
 * Create one simple internal geometry record in full-resolution image coordinates.
 *
 * The geometry record's coordinates are projected into full-resolution image space
 * and the bbox is calculated to save on extra processing time.
 *
 * @param {'Point'|'LineString'|'Polygon'} type - Simple geometry type.
 * @param {*} coordinates - Geometry coordinates.
 * @param {object|null|undefined} properties - Feature properties.
 * @param {string|number|undefined} id - Feature id.
 * @returns {SimpleGeoJSONGeometry|null} Internal geometry record.
 */
function createGeometryRecord(type, coordinates, properties, id) {
    switch (type) {
        case 'Point':
            coordinates = projectCoordinates(coordinates);
            break;
        case 'LineString':
            coordinates = coordinates.map(projectCoordinates);
            break;
        case 'Polygon':
            coordinates = coordinates.map(ring => ring.map(projectCoordinates));
            break;
        default:
            return null;
    }

    const bbox = computeImageSpaceBbox(coordinates);

    return {
        type,
        coordinates,
        bbox,
        properties,
        id
    };
}

/**
 * Convert one source coordinates into full-resolution image coordinates.
 *
 * Source coordinates are linearly mapped from the GeoJSON bbox into the
 * tile source width and height. With bbox [0, 0, width, height], coordinates
 * are preserved. With explicit width and height, the bbox coordinates range is
 * rescaled into those destination dimensions.
 *
 * @param {number[]} coordinates - Raw source coordinates.
 * @returns {number[]} Image coordinates.
 */
function projectCoordinates(coordinates) {
    // GeoJSON coordinates may contain z or other extra dimensions. Rendering is 2D.
    const [x, y] = coordinates;
    const [minX, minY, maxX, maxY] = STATE.bbox;

    const u = (x - minX) / (maxX - minX);
    const v = (y - minY) / (maxY - minY);

    return [
        u * STATE.width,
        v * STATE.height
    ];
}

/**
 * Compute image-space bbox for coordinates.
 *
 * @param {*} coordinates - Point, line, or polygon coordinates.
 * @returns {number[]} Bbox as [minX, minY, maxX, maxY].
 */
function computeImageSpaceBbox(coordinates) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    forEachCoordinates(coordinates, (coordinate) => {
        minX = Math.min(minX, coordinate[0]);
        minY = Math.min(minY, coordinate[1]);
        maxX = Math.max(maxX, coordinate[0]);
        maxY = Math.max(maxY, coordinate[1]);
    });

    return [minX, minY, maxX, maxY];
}

/**
 * Visit every coordinates in a supported coordinates structure.
 *
 * @param {*} coordinates - Coordinates structure.
 * @param {function(number[]): void} visitor - Coordinates visitor.
 * @returns {void}
 */
function forEachCoordinates(coordinates, visitor) {
    if (!Array.isArray(coordinates)) {
        return;
    }

    if (isCoordinates(coordinates)) {
        visitor(coordinates);
        return;
    }

    for (const child of coordinates) {
        forEachCoordinates(child, visitor);
    }
}

/**
 * Test whether a value is a valid GeoJSON linear ring.
 *
 * A linear ring is an array of at least four positions whose first and last
 * positions are equivalent. This check compares only x/y values because the
 * current renderer is 2D.
 *
 * @param {*} coordinates - Candidate linear-ring coordinates.
 * @returns {boolean} True when coordinates form a valid linear ring.
 */
function isLinearRing(coordinates) {
    if (Array.isArray(coordinates) && coordinates.length >= 4 && coordinates.every(isCoordinates)) {
        const first = coordinates[0];
        const last = coordinates[coordinates.length - 1];

        return first[0] === last[0] && first[1] === last[1];
    }

    return false;
}

/**
 * Test whether a value is a valid GeoJSON position.
 *
 * A position is an array of at least two finite numbers. The first two values
 * are interpreted as x/y by the current renderer; additional dimensions are
 * accepted but not rendered.
 *
 * @param {*} coordinate - Candidate position.
 * @returns {boolean} True when coordinate is a valid position.
 */
function isCoordinates(coordinate) {
    return Array.isArray(coordinate) && coordinate.length >= 2 && coordinate.every(Number.isFinite);
}


/**
 * Minimum projected geometry count before a spatial index is built.
 *
 * Small sources are usually faster to scan directly because the quadtree has
 * build cost and adds extra traversal during tile generation.
 *
 * @type {number}
 */
const SPATIAL_INDEX_MIN_GEOMETRIES = 256;

/**
 * Maximum number of geometries stored in one quadtree node before subdivision.
 *
 * @type {number}
 */
const QUADTREE_NODE_CAPACITY = 32;

/**
 * Maximum quadtree depth.
 *
 * This prevents excessive subdivision for highly clustered or nearly identical
 * bboxes.
 *
 * @type {number}
 */
const QUADTREE_MAX_DEPTH = 16;

/**
 * @typedef {object} QuadtreeNode
 * @property {number} depth - The depth of the quadtree node.
 * @property {number[]} bounds - The bounds of the quadtree node as [minX, minY, maxX, maxY].
 * @property {SimpleGeoJSONGeometry[]} geometries - The geometries assigned to this node.
 * @property {QuadtreeNode[]|null} children - The children nodes of this node or null if it is a leaf node.
 */

/**
 * Create a static quadtree spatial index for projected geometries.
 *
 * The quadtree is built in full-resolution image coordinates. It stores
 * references to projected geometry records, not copies. Geometry insertion is
 * conservative: a geometry is pushed into a child only when its bbox is fully
 * contained by that child; otherwise it remains in the current node.
 *
 * @param {SimpleGeoJSONGeometry[]} geometries - Projected geometry records.
 * @returns {QuadtreeNode|null} Quadtree root node, or null when direct scanning is preferable.
 */
function createSpatialIndex(geometries) {
    if (!Array.isArray(geometries) || geometries.length < SPATIAL_INDEX_MIN_GEOMETRIES) {
        return null;
    }

    const root = createQuadtreeNode([0, 0, STATE.width, STATE.height], 0);

    for (const geometry of geometries) {
        insertIntoQuadtree(root, geometry);
    }

    return root;
}

/**
 * Create one quadtree node.
 *
 * @param {number[]} bounds - Node bounds as [minX, minY, maxX, maxY].
 * @param {number} depth - Node depth.
 * @returns {QuadtreeNode} Quadtree node.
 */
function createQuadtreeNode(bounds, depth) {
    return {
        bounds,
        depth,
        geometries: [],
        children: null
    };
}

/**
 * Insert one geometry into a quadtree node.
 *
 * @param {QuadtreeNode} node - Quadtree node.
 * @param {SimpleGeoJSONGeometry} geometry - Projected geometry record.
 * @returns {void}
 */
function insertIntoQuadtree(node, geometry) {
    if (node.children) {
        const child = findContainingChild(node.children, geometry.bbox);

        if (child) {
            insertIntoQuadtree(child, geometry);
            return;
        }
    }

    node.geometries.push(geometry);

    if (node.geometries.length > QUADTREE_NODE_CAPACITY && node.depth < QUADTREE_MAX_DEPTH) {
        subdivideQuadtreeNode(node);
    }
}

/**
 * Subdivide one quadtree node and move contained geometries into children.
 *
 * Geometries that do not fit completely inside a single child remain in the
 * parent node. This avoids duplicating large lines and polygons across many
 * descendants.
 *
 * @param {QuadtreeNode} node - Quadtree node.
 * @returns {void}
 */
function subdivideQuadtreeNode(node) {
    if (node.children) {
        return;
    }

    const [minX, minY, maxX, maxY] = node.bounds;
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    const nextDepth = node.depth + 1;

    if (midX <= minX || midX >= maxX || midY <= minY || midY >= maxY) {
        return;
    }

    node.children = [
        createQuadtreeNode([minX, minY, midX, midY], nextDepth),
        createQuadtreeNode([midX, minY, maxX, midY], nextDepth),
        createQuadtreeNode([minX, midY, midX, maxY], nextDepth),
        createQuadtreeNode([midX, midY, maxX, maxY], nextDepth)
    ];

    const remaining = [];

    for (const geometry of node.geometries) {
        const child = findContainingChild(node.children, geometry.bbox);

        if (child) {
            insertIntoQuadtree(child, geometry);
        } else {
            remaining.push(geometry);
        }
    }

    node.geometries = remaining;
}

/**
 * Return the child that fully contains a bbox.
 *
 * @param {object[]} children - Quadtree child nodes.
 * @param {number[]} bbox - Geometry bbox as [minX, minY, maxX, maxY].
 * @returns {QuadtreeNode|null} Containing child node, or null when no single child contains the bbox.
 */
function findContainingChild(children, bbox) {
    for (const child of children) {
        if (containsBounds(child.bounds, bbox)) {
            return child;
        }
    }

    return null;
}

/**
 * Test whether outer bounds fully contain inner bounds.
 *
 * @param {number[]} outer - Outer bounds as [minX, minY, maxX, maxY].
 * @param {number[]} inner - Inner bounds as [minX, minY, maxX, maxY].
 * @returns {boolean} True when inner is fully inside outer.
 */
function containsBounds(outer, inner) {
    return inner[0] >= outer[0] && inner[1] >= outer[1] && inner[2] <= outer[2] && inner[3] <= outer[3];
}

/**
 * Return candidate geometries for a tile.
 *
 * If no spatial index is available, this returns the full geometry list and
 * preserves the previous direct-scan behavior.
 *
 * @param {number[]} tileBounds - Tile image-space bounds.
 * @returns {SimpleGeoJSONGeometry[]} Candidate geometry records.
 */
function getTileCandidateGeometries(tileBounds) {
    if (!STATE.spatialIndex) {
        return STATE.geometries;
    }

    const results = [];
    queryQuadtree(STATE.spatialIndex, tileBounds, results);

    return results;
}

/**
 * Query a quadtree node for geometries whose quadtree may intersect the query.
 *
 * @param {object} quadtree - Quadtree to query.
 * @param {number[]} bbox - Query bounds as [minX, minY, maxX, maxY].
 * @param {SimpleGeoJSONGeometry[]} results - Mutable result list.
 * @returns {void}
 */
function queryQuadtree(quadtree, bbox, results) {
    if (!intersects(bbox, quadtree.bounds)) {
        return;
    }

    for (const geometry of quadtree.geometries) {
        results.push(geometry);
    }

    if (!quadtree.children) {
        return;
    }

    for (const child of quadtree.children) {
        queryQuadtree(child, bbox, results);
    }
}


/**
 * Return one tile's full-resolution image bounds.
 *
 * Low pyramid levels and edge tiles can be smaller than tileSize in level-pixel
 * coordinates, so bounds must be derived from the actual clipped level rectangle.
 *
 * @param {number} level - OSD level.
 * @param {number} x - Tile x.
 * @param {number} y - Tile y.
 * @returns {number[]} Bounds as [minX, minY, maxX, maxY].
 */
function getTileImageBounds(level, x, y) {
    const scale = getLevelScale(level);
    const rect = getTileLevelRect(level, x, y);

    return [
        rect.left / scale,
        rect.top / scale,
        rect.right / scale,
        rect.bottom / scale
    ];
}

/**
 * Return the actual tile rectangle in level-pixel coordinates.
 *
 * For low pyramid levels, the whole level image can be smaller than tileSize.
 * For edge tiles, only part of the nominal tile rectangle is inside the level.
 *
 * @param {number} level - OSD level.
 * @param {number} x - Tile x.
 * @param {number} y - Tile y.
 * @returns {{left: number, top: number, right: number, bottom: number, width: number, height: number}}
 */
function getTileLevelRect(level, x, y) {
    const [levelWidth, levelHeight] = getLevelDimensions(level);
    const left = x * STATE.tileSize;
    const top = y * STATE.tileSize;
    const right = Math.min(left + STATE.tileSize, levelWidth);
    const bottom = Math.min(top + STATE.tileSize, levelHeight);

    return {
        left,
        top,
        right,
        bottom,
        width: Math.max(1, right - left),
        height: Math.max(1, bottom - top)
    };
}

/**
 * Return the image dimensions at one pyramid level.
 *
 * @param {number} level - OSD level.
 * @returns {number[]} Level dimensions as [width, height].
 */
function getLevelDimensions(level) {
    const scale = getLevelScale(level);

    return [
        Math.max(1, Math.ceil(STATE.width * scale)),
        Math.max(1, Math.ceil(STATE.height * scale))
    ];
}

/**
 * Return the OpenSeadragon pyramid scale for a level.
 *
 * @param {number} level - OSD level.
 * @returns {number} Level scale.
 */
function getLevelScale(level) {
    return 1 / Math.pow(2, STATE.maxLevel - level);
}

/**
 * Return the packed tile-depth value used by the vector renderer.
 *
 * @param {number} level - OSD level.
 * @param {number} x - Tile x.
 * @param {number} y - Tile y.
 * @returns {number} Tile depth value.
 */
function getTileDepth(level, x, y) {
    return (level << 2) + (2 * (y % 2) + (x % 2)) + 1;
}

/**
 * Test whether two bounds intersect.
 *
 * @param {number[]} a - First bounds.
 * @param {number[]} b - Second bounds.
 * @returns {boolean} True when the rectangles intersect.
 */
function intersects(a, b) {
    return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

/**
 * Test whether an image-space point is inside image-space bounds.
 *
 * @param {number[]} point - Image-space point.
 * @param {number[]} bounds - Bounds as [minX, minY, maxX, maxY].
 * @returns {boolean} True when the point is inside or on the bounds edge.
 */
function pointInBounds(point, bounds) {
    return point[0] >= bounds[0] && point[0] <= bounds[2] && point[1] >= bounds[1] && point[1] <= bounds[3];
}

/**
 * Clip a LineString to rectangular image-space bounds.
 *
 * Each visible segment is returned as a separate two-point LineString. This
 * avoids connecting disjoint clipped segments after clipping.
 *
 * @param {number[][]} coordinates - Image-space LineString coordinates.
 * @param {number[]} bounds - Bounds as [minX, minY, maxX, maxY].
 * @returns {number[][][]} Clipped LineString segments.
 */
function clipLineStringToBounds(coordinates, bounds) {
    const clipped = [];

    for (let i = 0; i < coordinates.length - 1; i += 1) {
        const segment = clipLineSegmentToBounds(coordinates[i], coordinates[i + 1], bounds);

        if (segment) {
            clipped.push(segment);
        }
    }

    return clipped;
}

/**
 * Clip one line segment to rectangular image-space bounds.
 *
 * Uses Liang-Barsky segment clipping.
 *
 * @param {number[]} start - Segment start point.
 * @param {number[]} end - Segment end point.
 * @param {number[]} bounds - Bounds as [minX, minY, maxX, maxY].
 * @returns {?number[][]} Clipped two-point segment, or null when outside.
 */
function clipLineSegmentToBounds(start, end, bounds) {
    const [minX, minY, maxX, maxY] = bounds;
    const x0 = start[0];
    const y0 = start[1];
    const x1 = end[0];
    const y1 = end[1];
    const dx = x1 - x0;
    const dy = y1 - y0;

    let t0 = 0;
    let t1 = 1;

    const clip = (p, q) => {
        if (p === 0) {
            return q >= 0;
        }

        const r = q / p;

        if (p < 0) {
            if (r > t1) {
                return false;
            }

            if (r > t0) {
                t0 = r;
            }
        } else {
            if (r < t0) {
                return false;
            }

            if (r < t1) {
                t1 = r;
            }
        }

        return true;
    };

    if (
        clip(-dx, x0 - minX) &&
        clip(dx, maxX - x0) &&
        clip(-dy, y0 - minY) &&
        clip(dy, maxY - y0)
    ) {
        return [
            [x0 + t0 * dx, y0 + t0 * dy],
            [x0 + t1 * dx, y0 + t1 * dy]
        ];
    }

    return null;
}

/**
 * Clip a Polygon to rectangular image-space bounds.
 *
 * The first ring is clipped as the exterior ring. Following rings are clipped
 * as hole rings and passed through to earcut.
 *
 * @param {number[][][]} coordinates - Polygon image-space coordinates.
 * @param {number[]} bounds - Bounds as [minX, minY, maxX, maxY].
 * @returns {?number[][][]} Clipped Polygon coordinates, or null when outside.
 */
function clipPolygonToBounds(coordinates, bounds) {
    if (!coordinates || coordinates.length === 0) {
        return null;
    }

    const clippedRings = [];

    for (let ringIndex = 0; ringIndex < coordinates.length; ringIndex += 1) {
        const clippedRing = clipRingToBounds(coordinates[ringIndex], bounds);

        if (clippedRing.length >= 4) {
            clippedRings.push(clippedRing);
        }
    }

    if (!clippedRings.length) {
        return null;
    }

    return clippedRings;
}

/**
 * Clip one polygon ring to rectangular image-space bounds.
 *
 * Uses Sutherland-Hodgman clipping against the four rectangle edges.
 *
 * @param {number[][]} ring - Closed polygon ring.
 * @param {number[]} bounds - Bounds as [minX, minY, maxX, maxY].
 * @returns {number[][]} Closed clipped ring, or an empty array.
 */
function clipRingToBounds(ring, bounds) {
    if (!Array.isArray(ring) || ring.length < 4) {
        return [];
    }

    const [minX, minY, maxX, maxY] = bounds;
    let output = ring.slice(0, -1);

    output = clipRingToBoundary(
        output,
        point => point[0] >= minX,
        (start, end) => intersectVertical(start, end, minX)
    );

    output = clipRingToBoundary(
        output,
        point => point[0] <= maxX,
        (start, end) => intersectVertical(start, end, maxX)
    );

    output = clipRingToBoundary(
        output,
        point => point[1] >= minY,
        (start, end) => intersectHorizontal(start, end, minY)
    );

    output = clipRingToBoundary(
        output,
        point => point[1] <= maxY,
        (start, end) => intersectHorizontal(start, end, maxY)
    );

    if (output.length < 3) {
        return [];
    }

    output.push(output[0].slice());

    return output;
}

/**
 * Clip one polygon ring against one half-plane.
 *
 * @param {number[][]} ring - Ring without duplicate closing coordinate.
 * @param {function(number[]): boolean} isInside - Boundary inclusion test.
 * @param {function(number[], number[]): number[]} intersect - Boundary intersection function.
 * @returns {number[][]} Clipped ring without duplicate closing coordinate.
 */
function clipRingToBoundary(ring, isInside, intersect) {
    if (!ring.length) {
        return [];
    }

    const clipped = [];

    for (let i = 0; i < ring.length; i += 1) {
        const current = ring[i];
        const previous = ring[(i + ring.length - 1) % ring.length];
        const currentInside = isInside(current);
        const previousInside = isInside(previous);

        if (currentInside) {
            if (!previousInside) {
                clipped.push(intersect(previous, current));
            }

            clipped.push(current);
        } else if (previousInside) {
            clipped.push(intersect(previous, current));
        }
    }

    return clipped;
}

/**
 * Intersect a segment with a vertical line.
 *
 * @param {number[]} start - Segment start.
 * @param {number[]} end - Segment end.
 * @param {number} x - Vertical line x coordinate.
 * @returns {number[]} Intersection point.
 */
function intersectVertical(start, end, x) {
    const dx = end[0] - start[0];

    if (dx === 0) {
        return [x, start[1]];
    }

    const t = (x - start[0]) / dx;

    return [
        x,
        start[1] + t * (end[1] - start[1])
    ];
}

/**
 * Intersect a segment with a horizontal line.
 *
 * @param {number[]} start - Segment start.
 * @param {number[]} end - Segment end.
 * @param {number} y - Horizontal line y coordinate.
 * @returns {number[]} Intersection point.
 */
function intersectHorizontal(start, end, y) {
    const dy = end[1] - start[1];

    if (dy === 0) {
        return [start[0], y];
    }

    const t = (y - start[1]) / dy;

    return [
        start[0] + t * (end[0] - start[0]),
        y
    ];
}


/**
 * Convert image coordinates into normalized tile-local coordinates.
 *
 * @param {number[]} coordinate - Image coordinate.
 * @param {object} tile - Tile request.
 * @returns {number[]} Tile-local coordinate normalized to the actual tile rectangle.
 */
function imageToTileUv(coordinate, tile) {
    const scale = getLevelScale(tile.level);
    const rect = getTileLevelRect(tile.level, tile.x, tile.y);
    const levelX = coordinate[0] * scale;
    const levelY = coordinate[1] * scale;
    const localX = levelX - rect.left;
    const localY = levelY - rect.top;

    return [
        localX / rect.width,
        localY / rect.height
    ];
}

/**
 * Convert a level-pixel size to normalized tile-local units.
 *
 * Mesh coordinates are emitted in normalized tile-local coordinates, where 1.0
 * equals the actual tile width or height at the requested level.
 *
 * @param {number} size - Size in level pixels.
 * @param {object} tile - Tile request.
 * @returns {number} Size in normalized tile-local units.
 */
function tilePixelSizeToUv(size, tile) {
    const rect = getTileLevelRect(tile.level, tile.x, tile.y);
    const denominator = Math.max(rect.width, rect.height);

    return size / denominator;
}


/**
 * Build a tile after configuration completes.
 *
 * @param {object} message - Tile request.
 * @returns {Promise<void>} Resolves after response posting.
 */
async function buildTileWhenReady(message) {
    try {
        if (STATE.configurePromise) {
            await STATE.configurePromise;
        }

        if (!STATE.configured) {
            throw new Error(STATE.fatalError || 'GeoJSON worker: received tile request before valid configuration.');
        }

        buildTile(message);
    } catch (error) {
        self.postMessage({
            type: 'tile',
            key: message.key,
            ok: false,
            error: error.message || String(error)
        });
    }
}

/**
 * Build one vector-mesh tile.
 *
 * @param {object} tile - Tile request.
 * @returns {void}
 */
function buildTile(tile) {
    const tileBounds = getTileImageBounds(tile.level, tile.x, tile.y);
    const depth = getTileDepth(tile.level, tile.x, tile.y);

    const output = {
        fills: [],
        lines: [],
        linePrimitives: [],
        points: []
    };

    const transfers = [];

    const { visible: visibleGeometries, contributingCandidates, drops } = getVisibleTileGeometries(tileBounds);

    const aggregated = shouldAggregateTile(tile, visibleGeometries.length);

    if (aggregated) {
        buildAggregateTile(tile, depth, visibleGeometries.length, output, transfers);
    } else {
        buildGeometryTile(tile, depth, visibleGeometries, output, transfers);
    }

    // An empty build is only suspicious when a candidate clipped to real coverage (positive
    // area or length) yet nothing meshed — "data expected here, none produced". It is posted
    // as a successful tile carrying a suspicious flag so the drawer can surface it as a
    // diagnostic (visible when render diagnostics are on) instead of a silent blank. A tile
    // made only of zero-area boundary grazes (a neighbouring polygon touching this tile's
    // edge) has no contributing candidate and is genuinely empty: it stays an ordinary empty
    // tile that draws nothing, exactly as before this guard existed.
    const empty = output.fills.length === 0 && output.lines.length === 0 &&
        output.linePrimitives.length === 0 && output.points.length === 0;
    const suspicious = empty && contributingCandidates > 0;

    if (suspicious && STATE.debug) {
        // eslint-disable-next-line no-console
        console.warn(
            \`GeoJSON worker: tile \${tile.level}/\${tile.x}/\${tile.y} had \` +
            \`\${contributingCandidates} geometries with real coverage but meshed nothing.\`,
            {
                level: tile.level,
                x: tile.x,
                y: tile.y,
                tileBounds,
                contributingCandidates,
                drops,
                aggregated
            }
        );
    }

    self.postMessage({
        type: 'tile',
        key: tile.key,
        ok: true,
        suspicious: suspicious,
        data: output
    }, transfers);
}

/**
 * Smallest clipped coverage that still counts as real geometry.
 *
 * A polygon that merely grazes a tile's shared boundary edge clips to a collinear,
 * zero-area ring (which earcut then triangulates to nothing); a line that grazes a corner
 * clips to a zero-length segment. Their measured area/length is 0 up to floating-point
 * noise, so anything at or below this threshold is treated as no coverage rather than a
 * missing render. Real coverage is many orders of magnitude larger (image-space pixels).
 *
 * @type {number}
 */
const CLIP_COVERAGE_EPSILON = 1e-6;

/**
 * Absolute shoelace area of a ring in image-space square pixels.
 *
 * @param {number[][]} ring - Polygon ring; the closing duplicate vertex, if present, does
 *     not affect the result.
 * @returns {number} Non-negative ring area.
 */
function ringArea(ring) {
    if (!Array.isArray(ring) || ring.length < 3) {
        return 0;
    }

    let sum = 0;

    for (let i = 0; i < ring.length; i += 1) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        sum += (a[0] * b[1]) - (b[0] * a[1]);
    }

    return Math.abs(sum) / 2;
}

/**
 * Total length of one or more clipped polylines in image-space pixels.
 *
 * @param {number[][][]} segments - Array of polylines, each an array of points.
 * @returns {number} Non-negative total length.
 */
function polylineLength(segments) {
    let total = 0;

    for (const segment of segments) {
        for (let i = 1; i < segment.length; i += 1) {
            const dx = segment[i][0] - segment[i - 1][0];
            const dy = segment[i][1] - segment[i - 1][1];
            total += Math.sqrt((dx * dx) + (dy * dy));
        }
    }

    return total;
}

/**
 * Return geometries that actually contribute to a tile.
 *
 * The candidate list comes from the quadtree when available. This function then
 * applies the same geometry-specific inclusion and clipping checks used by the
 * previous direct rendering path.
 *
 * @param {number[]} tileBounds - Tile image-space bounds.
 * @returns {{visible: object[], contributingCandidates: number, drops: {points: number, lines: number, polygons: number}}}
 *     The visible geometries with their clipped variants, plus a diagnostic summary.
 *     \`contributingCandidates\` counts candidates that clip to real coverage (positive area
 *     or length) — i.e. geometry that is expected to mesh. \`drops\` counts candidates whose
 *     bbox overlapped the tile yet clipped away to nothing, including zero-area boundary
 *     grazes. \`contributingCandidates\` drives the suspicious-empty detection in \`buildTile\`:
 *     an empty build with a contributing candidate is a real anomaly, whereas an empty
 *     build made only of grazes is genuinely empty and stays a normal cached success.
 */
function getVisibleTileGeometries(tileBounds) {
    const visible = [];

    // A candidate "contributes" only when it clips to real coverage. A polygon whose bbox
    // merely touches the tile boundary clips to a zero-area ring that earcut drops to
    // nothing — legitimately empty, not a bug — so it must not count toward suspicion.
    let contributingCandidates = 0;
    const drops = { points: 0, lines: 0, polygons: 0 };

    // Candidate geometries are read from a static image-space quadtree when the
    // source is large enough to justify indexing. Small sources fall back to
    // direct scanning.
    //
    // The quadtree is only an acceleration structure. Each candidate is still
    // bbox-checked and then clipped/meshed by the existing geometry-specific
    // paths, preserving previous rendering behavior.
    const candidateGeometries = getTileCandidateGeometries(tileBounds);

    // Geometries are bbox-filtered first, then clipped to the current tile before meshing.
    // This prevents large geometries from being emitted into every intersecting tile in full.
    for (const geometry of candidateGeometries) {
        if (!intersects(geometry.bbox, tileBounds)) {
            continue;
        }

        if (geometry.type === 'Point') {
            if (pointInBounds(geometry.coordinates, tileBounds)) {
                visible.push({
                    geometry
                });
                contributingCandidates++;
            } else {
                drops.points++;
            }
        } else if (geometry.type === 'LineString') {
            const clippedLines = clipLineStringToBounds(geometry.coordinates, tileBounds);

            if (clippedLines.length) {
                visible.push({
                    geometry,
                    clippedLines
                });

                if (polylineLength(clippedLines) > CLIP_COVERAGE_EPSILON) {
                    contributingCandidates++;
                }
            } else {
                drops.lines++;
            }
        } else if (geometry.type === 'Polygon') {
            const clippedPolygon = clipPolygonToBounds(geometry.coordinates, tileBounds);

            if (clippedPolygon) {
                visible.push({
                    geometry,
                    clippedPolygon
                });

                if (ringArea(clippedPolygon[0]) > CLIP_COVERAGE_EPSILON) {
                    contributingCandidates++;
                }
            } else {
                drops.polygons++;
            }
        }
    }

    return { visible, contributingCandidates, drops };
}

/**
 * Test whether a tile should be rendered as one aggregate marker.
 *
 * Aggregation is disabled at max level so the deepest available level always
 * renders the original annotation geometry.
 *
 * @param {object} tile - Tile request.
 * @param {number} count - Visible annotation count.
 * @returns {boolean} True when the tile should be aggregated.
 */
function shouldAggregateTile(tile, count) {
    return !!(STATE.aggregation && STATE.aggregation.enabled && tile.level < STATE.maxLevel && count > STATE.aggregation.threshold);
}

/**
 * Parse a color from any of the encodings producers commonly emit.
 *
 * Supported forms:
 *   - CSS hex string: '#rgb', '#rgba', '#rrggbb', '#rrggbbaa' (leading '#' optional)
 *   - Array of 3 or 4 finite numbers, either 0..1 floats or 0-255 components
 *   - Packed signed 32-bit ARGB integer, as written by QuPath
 *
 * Numeric arrays are ambiguous: [1, 0, 0] is valid in both scales. The rule is
 * that an array is read as 0..1 floats when every component is <= 1, and as
 * 0-255 otherwise. This keeps [0, 0, 0, 1] meaning opaque black rather than
 * near-transparent black, at the cost of making 0-255 near-black unwritable.
 * Use hex if you need it.
 *
 * Returns null rather than throwing so per-feature resolution can fall through
 * to the next precedence tier instead of failing a tile.
 *
 * Mirrored from src/geojson-tile-source.js. This worker is built standalone and
 * cannot import from there. Keep the two copies identical.
 *
 * @param {*} value - Candidate color.
 * @returns {?number[]} Color as [r, g, b, a] in 0..1, or null when unparseable.
 */
function parseColor(value) {
    if (typeof value === 'string') {
        const hex = value.trim().replace(/^#/, '');
        const expand = hex.length === 3 || hex.length === 4
            ? hex.split('').map(c => c + c).join('')
            : hex;

        if ((expand.length !== 6 && expand.length !== 8) || !/^[0-9a-fA-F]+$/.test(expand)) {
            return null;
        }

        const parts = expand.match(/../g).map(byte => parseInt(byte, 16) / 255);
        return [parts[0], parts[1], parts[2], parts.length === 4 ? parts[3] : 1];
    }

    if (typeof value === 'number') {
        if (!Number.isFinite(value) || !Number.isInteger(value)) {
            return null;
        }

        const alphaByte = (value >>> 24) & 0xFF;
        return [
            ((value >>> 16) & 0xFF) / 255,
            ((value >>> 8) & 0xFF) / 255,
            (value & 0xFF) / 255,
            // QuPath stores RGB-only colors with a zero alpha byte; treat those as opaque.
            alphaByte === 0 ? 1 : alphaByte / 255
        ];
    }

    if (Array.isArray(value)) {
        if ((value.length !== 3 && value.length !== 4) || !value.every(Number.isFinite)) {
            return null;
        }

        const scale = value.every(component => component <= 1) ? 1 : 255;
        return [
            value[0] / scale,
            value[1] / scale,
            value[2] / scale,
            value.length === 4 ? value[3] / scale : 1
        ];
    }

    return null;
}

/**
 * Read a dotted property path off an object.
 *
 * @param {*} object - Source object, possibly null or undefined.
 * @param {string} path - Dotted path, for example 'classification.color'.
 * @returns {*} The value at the path, or undefined when any segment is missing.
 */
function getPath(object, path) {
    if (!object || typeof path !== 'string') {
        return undefined;
    }

    let current = object;

    for (const segment of path.split('.')) {
        if (current === null || current === undefined || typeof current !== 'object') {
            return undefined;
        }

        current = current[segment];
    }

    return current;
}

/**
 * Sample a resolved colormap ramp.
 *
 * Stops arrive pre-resolved from the tile source as literal RGBA arrays, because
 * src/colormaps.js needs the OpenSeadragon global and this worker is standalone.
 *
 * @param {object} colormap - Normalized colormap spec with domain and stops.
 * @param {number} value - Raw score value.
 * @returns {?number[]} Interpolated color as [r, g, b, a], or null when value is not numeric.
 */
function sampleColormap(colormap, value) {
    if (!Number.isFinite(value)) {
        return null;
    }

    const [min, max] = colormap.domain;
    const stops = colormap.stops;

    if (max === min) {
        return stops[0];
    }

    const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
    const scaled = t * (stops.length - 1);
    const lower = Math.floor(scaled);
    const upper = Math.min(lower + 1, stops.length - 1);
    const frac = scaled - lower;

    const a = stops[lower];
    const b = stops[upper];

    return [
        a[0] + (b[0] - a[0]) * frac,
        a[1] + (b[1] - a[1]) * frac,
        a[2] + (b[2] - a[2]) * frac,
        a[3] + (b[3] - a[3]) * frac
    ];
}

/**
 * Resolve a feature's color from its properties.
 *
 * Precedence, first hit wins:
 *   1. classes[properties[classProperty]] - a label lookup, which lets a caller
 *      recolor at runtime via setStyle without re-exporting the source data.
 *   2. colorProperties - the first listed path holding a parseable color. This is
 *      the color the producer baked into the file.
 *   3. colormap - ramp a numeric property through pre-resolved stops.
 *   4. fallbackColor - the source-level flat color, which is the historical behavior.
 *
 * Never throws: an unparseable value falls through to the next tier so one bad
 * feature cannot fail a tile.
 *
 * @param {object|null|undefined} properties - Feature properties.
 * @param {number[]} fallbackColor - Per-geometry-type default color.
 * @returns {number[]} Color as [r, g, b, a] in 0..1.
 */
function resolveFeatureColor(properties, fallbackColor) {
    const style = STATE.style;

    if (!properties) {
        return fallbackColor;
    }

    if (style.classes && style.classProperty) {
        const label = getPath(properties, style.classProperty);

        if (label !== undefined && label !== null && Object.prototype.hasOwnProperty.call(style.classes, label)) {
            // Class colors are normalized to RGBA arrays by the tile source.
            return style.classes[label];
        }
    }

    if (style.colorProperties) {
        for (const path of style.colorProperties) {
            const parsed = parseColor(getPath(properties, path));

            if (parsed) {
                return parsed;
            }
        }
    }

    if (style.colormap) {
        const sampled = sampleColormap(style.colormap, getPath(properties, style.colormap.property));

        if (sampled) {
            return sampled;
        }
    }

    return fallbackColor;
}

/**
 * Resolve a geometry record's color, memoized on the record.
 *
 * A feature spanning many tiles would otherwise re-resolve on every tile build.
 * The cache is keyed by STATE.styleEpoch so setStyle invalidates it without
 * having to walk every record.
 *
 * @param {object} geometry - Internal geometry record.
 * @param {number[]} fallbackColor - Per-geometry-type default color.
 * @returns {number[]} Color as [r, g, b, a] in 0..1.
 */
function getGeometryColor(geometry, fallbackColor) {
    if (geometry._colorEpoch !== STATE.styleEpoch) {
        geometry._color = resolveFeatureColor(geometry.properties, fallbackColor);
        geometry._colorEpoch = STATE.styleEpoch;
    }

    return geometry._color;
}

/**
 * Build normal annotation meshes for a tile.
 *
 * @param {object} tile - Tile request.
 * @param {number} depth - Depth value.
 * @param {object[]} visibleGeometries - Array of objects containing the full visible geometries and their clipped variants.
 * @param {object} output - Mesh output object.
 * @param {ArrayBuffer[]} transfers - Transferable buffers.
 * @returns {void}
 */
function buildGeometryTile(tile, depth, visibleGeometries, output, transfers) {
    for (const item of visibleGeometries) {
        const geometry = item.geometry;

        switch (geometry.type) {
            case 'Point': {
                const color = getGeometryColor(geometry, STATE.style.pointColor);
                const mesh = makePointMesh(geometry.coordinates, tile, depth, STATE.style.pointSize, color);
                pushMesh(output.points, transfers, mesh);
                break;
            }

            case 'LineString': {
                const target = STATE.useNativeLines ? output.linePrimitives : output.lines;
                const color = getGeometryColor(geometry, STATE.style.lineColor);

                for (const clippedLine of item.clippedLines) {
                    const mesh = makeLineMesh(clippedLine, tile, depth, STATE.style.lineWidth, color);
                    pushMesh(target, transfers, mesh);
                }

                break;
            }

            case 'Polygon': {
                const color = getGeometryColor(geometry, STATE.style.fillColor);
                const mesh = makePolygonMesh(item.clippedPolygon, tile, depth, color);
                pushMesh(output.fills, transfers, mesh);
                break;
            }
        }
    }
}

/**
 * Build one aggregate badge tile.
 *
 * @param {object} tile - Tile request.
 * @param {number} depth - Depth value.
 * @param {number} count - Visible geometry count.
 * @param {object} output - Mesh output object.
 * @param {ArrayBuffer[]} transfers - Transferable buffers.
 * @returns {void}
 */
function buildAggregateTile(tile, depth, count, output, transfers) {
    const tileBounds = getTileImageBounds(tile.level, tile.x, tile.y);
    const center = [ (tileBounds[0] + tileBounds[2]) / 2, (tileBounds[1] + tileBounds[3]) / 2 ];
    const badgeMesh = makeAggregateBadgeMesh(center, tile, depth, count);

    pushMesh(output.fills, transfers, badgeMesh);

    const labelTarget = STATE.useNativeLines ? output.linePrimitives : output.lines;

    for (const labelMesh of makeAggregateLabelMeshes(center, tile, depth, count)) {
        pushMesh(labelTarget, transfers, labelMesh);
    }
}


/**
 * Create a filled octagonal aggregate badge mesh.
 *
 * @param {number[]} center - Badge center in image coordinates.
 * @param {object} tile - Tile request.
 * @param {number} depth - Depth value.
 * @returns {object} Mesh object.
 */
function makeAggregateBadgeMesh(center, tile, depth) {
    const [x, y] = imageToTileUv(center, tile);
    const rect = getTileLevelRect(tile.level, tile.x, tile.y);
    const radiusX = (STATE.aggregation.badgeSize / 2) / rect.width;
    const radiusY = (STATE.aggregation.badgeSize / 2) / rect.height;
    const vertices = [
        x, y, depth, -1
    ];
    const indices = [];
    const sides = 8;

    for (let i = 0; i < sides; i += 1) {
        const angle = -Math.PI / 2 + (i / sides) * Math.PI * 2;

        vertices.push(
            x + Math.cos(angle) * radiusX,
            y + Math.sin(angle) * radiusY,
            depth,
            -1
        );
    }

    for (let i = 1; i <= sides; i += 1) {
        indices.push(0, i, i === sides ? 1 : i + 1);
    }

    return makeMesh(vertices, indices, STATE.aggregation.badgeColor);
}

/**
 * Create line meshes for an aggregate badge count label.
 *
 * @param {number[]} center - Label center in image coordinates.
 * @param {object} tile - Tile request.
 * @param {number} depth - Depth value.
 * @param {number} count - Visible annotation count.
 * @returns {object[]} Label line meshes.
 */
function makeAggregateLabelMeshes(center, tile, depth, count) {
    const label = count > STATE.aggregation.maxLabelValue ? \`\${STATE.aggregation.maxLabelValue}+\` : String(count);
    const scale = getLevelScale(tile.level);
    const height = STATE.aggregation.labelSize / scale;
    const width = height * 0.55;
    const gap = height * 0.22;
    const totalWidth = label.length * width + Math.max(0, label.length - 1) * gap;
    const startX = center[0] - totalWidth / 2 + width / 2;
    const meshes = [];

    for (let index = 0; index < label.length; index += 1) {
        const character = label[index];
        const glyphCenter = [
            startX + index * (width + gap),
            center[1]
        ];

        for (const segment of getGlyphSegments(character, glyphCenter, width, height)) {
            const mesh = makeLineMesh(
                segment,
                tile,
                depth,
                STATE.aggregation.labelStrokeWidth,
                STATE.aggregation.labelColor
            );

            if (mesh) {
                meshes.push(mesh);
            }
        }
    }

    return meshes;
}

/**
 * Return line segments for one seven-segment glyph.
 *
 * @param {string} character - Digit or plus sign.
 * @param {number[]} center - Glyph center in image coordinates.
 * @param {number} width - Glyph width in image coordinates.
 * @param {number} height - Glyph height in image coordinates.
 * @returns {number[][][]} Glyph line segments.
 */
function getGlyphSegments(character, center, width, height) {
    const segments = SEVEN_SEGMENT_GLYPHS[character];

    if (!segments) {
        return [];
    }

    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const midY = center[1];

    const points = {
        topLeft: [center[0] - halfWidth, center[1] - halfHeight],
        topRight: [center[0] + halfWidth, center[1] - halfHeight],
        middleLeft: [center[0] - halfWidth, midY],
        middleRight: [center[0] + halfWidth, midY],
        bottomLeft: [center[0] - halfWidth, center[1] + halfHeight],
        bottomRight: [center[0] + halfWidth, center[1] + halfHeight],
        topMiddle: [center[0], center[1] - halfHeight * 0.65],
        bottomMiddle: [center[0], center[1] + halfHeight * 0.65]
    };

    const segmentCoordinates = {
        top: [points.topLeft, points.topRight],
        upperRight: [points.topRight, points.middleRight],
        lowerRight: [points.middleRight, points.bottomRight],
        bottom: [points.bottomLeft, points.bottomRight],
        lowerLeft: [points.middleLeft, points.bottomLeft],
        upperLeft: [points.topLeft, points.middleLeft],
        middle: [points.middleLeft, points.middleRight],
        verticalMiddle: [points.topMiddle, points.bottomMiddle]
    };

    return segments.map(segment => segmentCoordinates[segment]);
}


/**
 * Create a square point marker mesh.
 *
 * @param {number[]} coordinate - Image coordinate.
 * @param {object} tile - Tile request.
 * @param {number} depth - Depth value.
 * @param {number} pointSize - Line width in level pixels.
 * @param {number[]} color - RGBA color.
 * @returns {?object} Mesh object.
 */
function makePointMesh(coordinate, tile, depth, pointSize, color) {
    const [x, y] = imageToTileUv(coordinate, tile);
    const size = tilePixelSizeToUv(pointSize, tile);
    const half = size / 2;

    return makeMesh(
        [
            x - half, y - half, depth, -1,
            x + half, y - half, depth, -1,
            x + half, y + half, depth, -1,
            x - half, y + half, depth, -1
        ],
        [0, 1, 2, 0, 2, 3],
        color
    );
}

/**
 * Create simple rectangular segment meshes for a styled line.
 *
 * @param {number[][]} coordinates - Image coordinates.
 * @param {object} tile - Tile request.
 * @param {number} depth - Depth value.
 * @param {number} lineWidth - Line width in level pixels.
 * @param {number[]} color - RGBA color.
 * @returns {?object} Mesh object.
 */
function makeLineMesh(coordinates, tile, depth, lineWidth, color) {
    if (!coordinates || coordinates.length < 2) {
        return null;
    }

    if (STATE.useNativeLines) {
        const vertices = [];
        const indices = [];

        for (let i = 0; i < coordinates.length; i++) {
            const point = imageToTileUv(coordinates[i], tile);

            vertices.push(point[0], point[1], depth, -1);

            if (i < coordinates.length - 1) {
                indices.push(i, i + 1);
            }
        }

        if (!indices.length) {
            return null;
        }

        const mesh = makeMesh(vertices, indices, color);
        mesh.lineWidth = Number.isFinite(lineWidth) && lineWidth > 0 ? lineWidth : 1;
        return mesh;
    }

    const vertices = [];
    const indices = [];
    const width = tilePixelSizeToUv(lineWidth, tile);
    const half = width / 2;

    for (let i = 0; i < coordinates.length - 1; i++) {
        const p0 = imageToTileUv(coordinates[i], tile);
        const p1 = imageToTileUv(coordinates[i + 1], tile);
        const dx = p1[0] - p0[0];
        const dy = p1[1] - p0[1];
        const length = Math.sqrt(dx * dx + dy * dy);

        if (!length) {
            continue;
        }

        const nx = -dy / length * half;
        const ny = dx / length * half;
        const base = vertices.length / 4;

        vertices.push(
            p0[0] - nx, p0[1] - ny, depth, -1,
            p0[0] + nx, p0[1] + ny, depth, -1,
            p1[0] + nx, p1[1] + ny, depth, -1,
            p1[0] - nx, p1[1] - ny, depth, -1
        );

        indices.push(
            base, base + 1, base + 2,
            base, base + 2, base + 3
        );
    }

    if (!indices.length) {
        return null;
    }

    return makeMesh(vertices, indices, color);
}

/**
 * Create a triangulated mesh for a Polygon.
 *
 * Earcut supports concave polygons and holes. The first ring is treated as the
 * exterior ring and all following rings are treated as holes, matching GeoJSON
 * Polygon coordinate structure.
 *
 * @param {number[][][]} coordinates - Polygon image coordinates.
 * @param {object} tile - Tile request.
 * @param {number} depth - Depth value.
 * @param {number[]} color - RGBA color.
 * @returns {?object} Mesh object.
 */
function makePolygonMesh(coordinates, tile, depth, color) {
    if (!self.earcut) {
        throw new Error('GeoJSON worker: missing earcut library.');
    }

    if (coordinates.length === 0) {
        return null;
    }

    const flat = [];
    const holes = [];
    const vertices = [];
    let vertexCount = 0;

    for (let ringIndex = 0; ringIndex < coordinates.length; ringIndex += 1) {
        const ring = coordinates[ringIndex].slice(0, -1);

        if (ringIndex > 0) {
            holes.push(vertexCount);
        }

        for (const coordinate of ring) {
            const point = imageToTileUv(coordinate, tile);

            flat.push(point[0], point[1]);
            vertices.push(point[0], point[1], depth, -1);
            vertexCount += 1;
        }
    }

    const indices = self.earcut(flat, holes, 2);

    if (!indices.length) {
        return null;
    }

    return makeMesh(vertices, indices, color);
}

/**
 * Create a vector mesh object with transferable buffers.
 *
 * @param {number[]} vertices - Flat vec4 vertex coordinates.
 * @param {number[]} indices - Triangle indices.
 * @param {number[]} color - RGBA color.
 * @returns {object} Mesh object.
 */
function makeMesh(vertices, indices, color) {
    return {
        vertices: new Float32Array(vertices).buffer,
        indices: new Uint32Array(indices).buffer,
        color
    };
}

/**
 * Push a mesh and its transferable buffers into tile output arrays.
 *
 * @param {object[]} target - Mesh target array.
 * @param {ArrayBuffer[]} transfers - Transferable buffers.
 * @param {?object} mesh - Mesh to push.
 * @returns {void}
 */
function pushMesh(target, transfers, mesh) {
    if (!mesh) {
        return;
    }

    target.push(mesh);
    transfers.push(mesh.vertices, mesh.indices);
}

`;
})(typeof self !== 'undefined' ? self : window);